import { useState, useEffect } from 'react';

/**
 * Shared utility for MFEs to wait for organization context from the Shell
 * This prevents race conditions where MFEs initialize before Shell sets window.__HAVEN_ORG_ID__
 */

/**
 * Get current organization context from URL or shell state
 */
export function getCurrentOrgId(): string | null {
  // Try to get orgId from URL path (/o/:orgId/...)
  const pathMatch = window.location.pathname.match(/^\/o\/([^\/]+)/);
  if (pathMatch) {
    return pathMatch[1];
  }
  
  // Fallback to shell global if available
  return (window as any).__HAVEN_ORG_ID__ || null;
}

/**
 * React hook for MFEs to consume organization context from the Shell
 * Uses event-based pattern with fallback to eliminate race conditions
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 15.5
 */
export function useOrganizationContext() {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let fallbackTimeout: ReturnType<typeof setTimeout>;

    // Event handler for haven:org-ready
    const handleOrgReady = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        orgId: string | null; 
        orgName?: string;
        role?: string;
        timestamp?: number;
        error?: string;
      }>;
      
      console.log('🔧 MFE: Received haven:org-ready event', customEvent.detail);

      // Handle error in event.detail
      if (customEvent.detail.error) {
        setError(customEvent.detail.error);
        setIsReady(true);
        return;
      }

      // Extract orgId from event.detail
      if (customEvent.detail.orgId) {
        setOrgId(customEvent.detail.orgId);
        setIsReady(true);
        setError(null);
      }
    };

    // Register event listener for haven:org-ready
    window.addEventListener('haven:org-ready', handleOrgReady);

    // Implement 100ms fallback to check window.__HAVEN_ORG_ID__
    fallbackTimeout = setTimeout(() => {
      if (!isReady) {
        const fallbackOrgId = (window as any).__HAVEN_ORG_ID__;
        if (fallbackOrgId) {
          console.log('🔧 MFE: Using fallback window.__HAVEN_ORG_ID__', fallbackOrgId);
          setOrgId(fallbackOrgId);
          setIsReady(true);
          setError(null);
        } else {
          console.log('🔧 MFE: Fallback check - no orgId available yet');
        }
      }
    }, 100);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('haven:org-ready', handleOrgReady);
      clearTimeout(fallbackTimeout);
    };
  }, [isReady]);

  return { orgId, isReady, error };
}

/**
 * Wait for organization context to be ready
 * Uses both polling and event listening for maximum reliability
 * 
 * @param timeoutMs - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise that resolves with orgId or rejects on timeout
 */
export async function waitForOrgContext(timeoutMs: number = 5000): Promise<string> {
  let orgId = getCurrentOrgId();
  
  // If we already have it, return immediately
  if (orgId) {
    console.log('🔍 MFE: Organization context already available:', orgId);
    return orgId;
  }
  
  console.warn('🔍 MFE: Organization context not ready, waiting...');
  console.log('🔍 MFE: Current window.__HAVEN_ORG_ID__:', (window as any).__HAVEN_ORG_ID__);
  console.log('🔍 MFE: URL pathname:', window.location.pathname);
  
  // Create a promise that resolves when the event is dispatched
  const eventPromise = new Promise<string>((resolve) => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ orgId: string }>;
      console.log('🔍 MFE: Received haven:org-ready event with orgId:', customEvent.detail.orgId);
      resolve(customEvent.detail.orgId);
    };
    window.addEventListener('haven:org-ready', handler, { once: true });
    
    // Clean up listener after timeout
    setTimeout(() => {
      window.removeEventListener('haven:org-ready', handler);
    }, timeoutMs);
  });
  
  // Polling fallback
  const maxAttempts = timeoutMs / 100;
  let attempts = 0;
  
  const pollingPromise = (async () => {
    while (!orgId && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      orgId = getCurrentOrgId();
      attempts++;
      
      // Log every 500ms (every 5 attempts)
      if (attempts % 5 === 0) {
        console.log(`🔍 MFE: Waiting for Org ID... Attempt ${attempts}/${maxAttempts} | Current value: ${(window as any).__HAVEN_ORG_ID__ || 'NULL'}`);
      }
      
      if (orgId) {
        return orgId;
      }
    }
    return null;
  })();
  
  // Wait for either the event or polling to succeed
  orgId = await Promise.race([eventPromise, pollingPromise]).catch(() => null);
  
  if (!orgId) {
    console.error('🔍 MFE: Organization context timeout after', timeoutMs, 'ms');
    console.error('🔍 MFE: Final window.__HAVEN_ORG_ID__:', (window as any).__HAVEN_ORG_ID__);
    console.error('🔍 MFE: Final URL:', window.location.pathname);
    console.error('🔍 MFE: Window object:', window);
    console.error('🔍 MFE: Are we in an iframe?', window !== window.top);
    console.error('🔍 MFE: Window.top:', window.top);
    throw new Error(`Organization context not available after ${timeoutMs}ms timeout`);
  }
  
  console.log('🔍 MFE: Organization context ready! OrgId:', orgId);
  return orgId;
}

/**
 * Get auth token from shell global
 */
export function getToken(): string | null {
  return (window as any).__HAVEN_TOKEN__ || null;
}

/**
 * Setup organization interceptor for fetch requests
 * Creates a configured fetch wrapper that automatically injects X-Org-ID header
 * 
 * Requirements: 7.5
 * 
 * @param orgId - The organization ID to inject in all requests
 * @returns Configured fetch function that includes X-Org-ID header
 * 
 * @example
 * ```typescript
 * const { orgId } = useOrganizationContext();
 * const orgFetch = setupOrganizationInterceptor(orgId);
 * 
 * // All requests will include X-Org-ID header
 * const response = await orgFetch('/api/documents');
 * ```
 */
export function setupOrganizationInterceptor(orgId: string): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Clone init to avoid mutating the original
    const enhancedInit: RequestInit = {
      ...init,
      headers: {
        ...init?.headers,
        'X-Org-ID': orgId,
      },
    };

    return fetch(input, enhancedInit);
  };
}

/**
 * Options for useOrganizationSwitch hook
 */
export interface UseOrganizationSwitchOptions {
  /**
   * Callback to clear cache when organization switches
   * This can be used with React Query, SWR, or any other caching library
   * 
   * @example
   * ```typescript
   * // With React Query
   * const queryClient = useQueryClient();
   * useOrganizationSwitch({
   *   onCacheClear: () => queryClient.clear()
   * });
   * 
   * // With custom cache
   * useOrganizationSwitch({
   *   onCacheClear: () => myCache.clear()
   * });
   * ```
   */
  onCacheClear?: () => void;

  /**
   * Callback to update HTTP interceptor when organization switches
   * This is called with the new orgId
   * 
   * @example
   * ```typescript
   * useOrganizationSwitch({
   *   onInterceptorUpdate: (orgId) => {
   *     axios.defaults.headers.common['X-Org-ID'] = orgId;
   *   }
   * });
   * ```
   */
  onInterceptorUpdate?: (orgId: string) => void;

  /**
   * Callback when organization switch is detected
   * Receives both old and new organization IDs
   * 
   * @example
   * ```typescript
   * useOrganizationSwitch({
   *   onSwitch: (oldOrgId, newOrgId) => {
   *     console.log(`Switched from ${oldOrgId} to ${newOrgId}`);
   *   }
   * });
   * ```
   */
  onSwitch?: (oldOrgId: string | null, newOrgId: string) => void;
}

/**
 * React hook for MFEs to detect organization switches and invalidate cached data
 * 
 * This hook:
 * - Tracks current orgId in state
 * - Listens for haven:org-ready events
 * - Detects organization changes
 * - Clears cache on change (via callback)
 * - Updates HTTP interceptor with new orgId (via callback)
 * - Logs organization switch with 🔧 prefix
 * 
 * Requirements: 7.6, 7.7, 8.5, 8.6, 15.5
 * 
 * @param options - Configuration options for cache clearing and interceptor updates
 * @returns Current organization ID
 * 
 * @example
 * ```typescript
 * // Basic usage with React Query
 * import { useQueryClient } from '@tanstack/react-query';
 * 
 * function MyMFE() {
 *   const queryClient = useQueryClient();
 *   const currentOrgId = useOrganizationSwitch({
 *     onCacheClear: () => queryClient.clear(),
 *     onInterceptorUpdate: (orgId) => {
 *       axios.defaults.headers.common['X-Org-ID'] = orgId;
 *     }
 *   });
 *   
 *   // Use currentOrgId in your component
 * }
 * 
 * // Usage without cache library (manual state management)
 * function MyMFE() {
 *   const [data, setData] = useState(null);
 *   
 *   const currentOrgId = useOrganizationSwitch({
 *     onCacheClear: () => setData(null),
 *     onSwitch: (oldOrgId, newOrgId) => {
 *       // Refetch data for new organization
 *       fetchData(newOrgId);
 *     }
 *   });
 * }
 * ```
 */
export function useOrganizationSwitch(options: UseOrganizationSwitchOptions = {}) {
  const { onCacheClear, onInterceptorUpdate, onSwitch } = options;
  
  // Track current orgId in state
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);

  useEffect(() => {
    // Listen for haven:org-ready events
    const handleOrgReady = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        orgId: string | null; 
        orgName?: string;
        role?: string;
        timestamp?: number;
        error?: string;
      }>;

      const newOrgId = customEvent.detail.orgId;

      // Ignore error events
      if (customEvent.detail.error || !newOrgId) {
        return;
      }

      // Detect organization changes
      if (newOrgId !== currentOrgId) {
        console.log('🔧 MFE: Organization switch detected', {
          from: currentOrgId,
          to: newOrgId
        });

        // Clear cache on change
        if (onCacheClear) {
          console.log('🔧 MFE: Clearing cache for organization switch');
          onCacheClear();
        }

        // Update HTTP interceptor with new orgId
        if (onInterceptorUpdate) {
          console.log('🔧 MFE: Updating HTTP interceptor with new orgId:', newOrgId);
          onInterceptorUpdate(newOrgId);
        }

        // Call onSwitch callback
        if (onSwitch) {
          onSwitch(currentOrgId, newOrgId);
        }

        // Update current orgId
        setCurrentOrgId(newOrgId);
      }
    };

    // Register event listener
    window.addEventListener('haven:org-ready', handleOrgReady);

    // Clean up on unmount
    return () => {
      window.removeEventListener('haven:org-ready', handleOrgReady);
    };
  }, [currentOrgId, onCacheClear, onInterceptorUpdate, onSwitch]);

  return currentOrgId;
}
